<script setup lang="ts">
import type { SchoolYear, SchoolYearForm } from '@/data/kindergarten/school-years/repository';
import { createEmpty, create as createSchoolYear, findAll, isEmpty, remove as removeSchoolYear, update as updateSchoolYear } from '@/data/kindergarten/school-years/repository';
import { onMounted, ref } from 'vue';
import type ApiError from '@legacy/js/model/Error';
import DatePicker from '../common/form/picker/DatePicker.vue';
import IconDelete from '../common/icon/IconDelete.vue';
import IconEdit from '../common/icon/IconEdit.vue';
import IconReset from '../common/icon/IconReset.vue';
import { SphSpinner } from '@/components/base/spinner';
import moment from 'moment';
import { useDialog } from '@/plugins/dialog';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
    components: {
        'kgr-icon-edit': IconEdit,
        'kgr-icon-reset': IconReset,
        'kgr-icon-delete': IconDelete,
        'kgr-date-picker': DatePicker,
    },
});

const props = withDefaults(defineProps<{
    kindergartenId: number
    nameMaxLength?: number
}>(), {
    nameMaxLength: 100,
});

const { dialog } = useDialog();
const { i18n } = useI18n();
const { notify } = useNotifications();

const loading = ref(true);
const schoolYears = ref<SchoolYear[]>([]);
const errors = ref<Record<number, ApiError>>({});
const justSaved = ref<Record<number, boolean>>({});
const newSchoolYear = ref<SchoolYearForm>(createEmpty(props.kindergartenId));
const editSchoolYears = ref<SchoolYearForm[]>([]);

async function load() {
    loading.value = true;
    try {
        schoolYears.value = await findAll(props.kindergartenId);
    } finally {
        loading.value = false;
    }
}

async function reload() {
    schoolYears.value = await findAll(props.kindergartenId);
}

onMounted(load);

function editing(schoolYear: SchoolYear): boolean {
    return getEditIndex(schoolYear) !== -1;
}

function getEdit(schoolYear: SchoolYear): SchoolYearForm {
    return editSchoolYears.value[getEditIndex(schoolYear)];
}

function getEditIndex(schoolYear: SchoolYear): number {
    return editSchoolYears.value.map((s) => s.id).indexOf(schoolYear.id);
}

function isValidName(schoolYear: SchoolYearForm): boolean {
    return schoolYear.name.length > 0 && schoolYear.name.length <= props.nameMaxLength && !hasErrorAtField(schoolYear, 'name');
}

function isValidStart(schoolYear: SchoolYearForm): boolean {
    return !!schoolYear.start && isValidDateRange(schoolYear) && !hasErrorAtField(schoolYear, 'start') && isValidDateString(schoolYear.start);
}

function isValidEnd(schoolYear: SchoolYearForm): boolean {
    return !!schoolYear.end && isValidDateRange(schoolYear) && !hasErrorAtField(schoolYear, 'end') && isValidDateString(schoolYear.end);
}

function isValidImmutableDate(schoolYear: SchoolYearForm): boolean {
    return !!schoolYear.immutableDate && isInRuntime(schoolYear) && !hasErrorAtField(schoolYear, 'immutableDate') && isValidDateString(schoolYear.immutableDate);
}

const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
function isValidDateString(date: string): boolean {
    return dateRegex.test(date);
}

function isValidDateRange(schoolYear: SchoolYearForm): boolean {
    if (!schoolYear.end || !schoolYear.start) {
        return false;
    }
    return moment(schoolYear.end, 'DD.MM.YYYY') >= moment(schoolYear.start, 'DD.MM.YYYY');
}

function isValid(schoolYear: SchoolYearForm): boolean {
    return isValidName(schoolYear) && isValidStart(schoolYear) && isValidEnd(schoolYear) && isValidImmutableDate(schoolYear);
}

function isInRuntime(schoolYear: SchoolYearForm): boolean {
    if (!isValidDateRange(schoolYear)) {
        return false;
    }
    return moment(schoolYear.immutableDate, 'DD.MM.YYYY') >= moment(schoolYear.start, 'DD.MM.YYYY')
        && moment(schoolYear.immutableDate, 'DD.MM.YYYY') <= moment(schoolYear.end, 'DD.MM.YYYY');
}

function edit(schoolYear: SchoolYear) {
    if (editing(schoolYear)) {
        return;
    }
    editSchoolYears.value.push({ ...schoolYear });
}

async function handleRemove(schoolYear: SchoolYear) {
    const confirmed = await dialog.confirm({
        title: i18n.t('confirm'),
        message: i18n.t('schoolYear.confirm.delete', { name: schoolYear.name }),
        confirmButtonLabel: i18n.t('ok'),
    });

    if (!confirmed) {
        return;
    }

    removeSchoolYear(schoolYear.id)
        .then(() => {
            editSchoolYears.value = editSchoolYears.value.filter((s) => s.id !== schoolYear.id);
            schoolYears.value = schoolYears.value.filter((s) => s.id !== schoolYear.id);
            notify.success('schoolYear.delete.success', { name: schoolYear.name });
            reload();
        })
        .catch((error: unknown) => {
            if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
                notify.error('schoolYear.delete.conflict', { name: schoolYear.name });
            } else {
                notify.error('saveChanges.error');
            }
        });
}

function create(schoolYear: SchoolYearForm) {
    createSchoolYear(schoolYear.name, schoolYear.start, schoolYear.end, schoolYear.immutableDate)
        .then(() => {
            newSchoolYear.value = createEmpty(props.kindergartenId);
            notify.success('saveChanges.success');
            reload();
        })
        .catch((error: ApiError) => handleError(schoolYear, error));
}

function update(schoolYear: SchoolYearForm) {
    updateSchoolYear(schoolYear)
        .then(() => {
            editSchoolYears.value = [];
            addJustSaved(schoolYear);
            reload();
        })
        .catch((error: ApiError) => handleError(schoolYear, error));
}

function reset(schoolYear: SchoolYearForm) {
    removeError(schoolYear);
    if (schoolYear.id) {
        editSchoolYears.value = editSchoolYears.value.filter((s) => s.id !== schoolYear.id);
    } else {
        newSchoolYear.value = createEmpty(props.kindergartenId);
    }
}

function handleError(schoolYear: SchoolYearForm, error: ApiError) {
    const id = schoolYear.id || 0;
    errors.value[id] = error;
    notify.error('saveChanges.error');
}

function hasErrorAtField(schoolYear: SchoolYearForm, field: string): boolean {
    const id = schoolYear.id || 0;
    if (id in errors.value) {
        return errors.value[id].forField(field);
    }
    return false;
}

function errorMessageForField(schoolYear: SchoolYearForm, field: string): string | false {
    const id = schoolYear.id || 0;
    return hasErrorAtField(schoolYear, field) ? errors.value[id].messageForField(field) : false;
}

function removeError(schoolYear: SchoolYearForm) {
    const id = schoolYear.id || 0;
    if (id in errors.value) {
        delete errors.value[id];
    }
}

function save(schoolYear: SchoolYearForm, field = '') {
    if ((hasErrorAtField(schoolYear, 'start') || hasErrorAtField(schoolYear, 'end')) && (field === 'start' || field === 'end')) {
        removeError(schoolYear);
    }

    if (isValid(schoolYear)) {
        schoolYear.id ? update(schoolYear) : create(schoolYear);
    }
}

function addJustSaved(schoolYear: SchoolYearForm) {
    const id = schoolYear.id || 0;
    justSaved.value[id] = true;
    triggerAnimation();
    setTimeout(() => removeJustSaved(schoolYear), 2000);
}

function removeJustSaved(schoolYear: SchoolYearForm) {
    const id = schoolYear.id || 0;
    if (id in justSaved.value) {
        justSaved.value[id] = false;
    }
}

function isJustSaved(schoolYear: SchoolYear): boolean {
    const id = schoolYear.id || 0;
    return justSaved.value[id] ?? false;
}

function triggerAnimation() {
    const elements = document.querySelectorAll('tr.justSaved td');
    elements.forEach((el) => (el as HTMLElement).style.animation = 'none');
    requestAnimationFrame(() => {
        elements.forEach((el) => (el as HTMLElement).style.animation = 'justSaved normal 1s 0s ease-in-out');
    });
}
</script>

<template>
    <div>
        <SphSpinner
            :show="loading"
            variant="centered"
        />
        <div v-if="!loading">
            <table class="kgr-table kgr-table--block kgr-table--striped">
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('name') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('start') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('end') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('bookingNote.closingDate') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions">
                            {{ i18n.t('action') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="kgr-table__row">
                        <td class="kgr-table__cell">
                            <input
                                v-model="newSchoolYear.name"
                                type="text"
                                class="kgr-input"
                                :maxlength="props.nameMaxLength"
                                :class="{ 'is-invalid': !isEmpty(newSchoolYear) && !isValidName(newSchoolYear) }"
                                @blur="save(newSchoolYear)"
                            />
                            <div
                                v-if="hasErrorAtField(newSchoolYear, 'name')"
                                class="errors kgr-color kgr-color--error"
                            >
                                {{ errorMessageForField(newSchoolYear, 'name') }}
                            </div>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-date-picker
                                v-model="newSchoolYear.start"
                                :invalid="!isEmpty(newSchoolYear) && !isValidStart(newSchoolYear)"
                                @post-input="save(newSchoolYear, 'start')"
                            />
                            <div
                                v-if="hasErrorAtField(newSchoolYear, 'start')"
                                class="errors kgr-color kgr-color--error"
                            >
                                {{ errorMessageForField(newSchoolYear, 'start') }}
                            </div>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-date-picker
                                v-model="newSchoolYear.end"
                                :invalid="!isEmpty(newSchoolYear) && !isValidEnd(newSchoolYear)"
                                @post-input="save(newSchoolYear, 'end')"
                            />
                            <div
                                v-if="hasErrorAtField(newSchoolYear, 'end')"
                                class="errors kgr-color kgr-color--error"
                            >
                                {{ errorMessageForField(newSchoolYear, 'end') }}
                            </div>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-date-picker
                                v-model="newSchoolYear.immutableDate"
                                :invalid="!isEmpty(newSchoolYear) && !isValidImmutableDate(newSchoolYear)"
                                @post-input="save(newSchoolYear)"
                            />
                            <div
                                v-if="hasErrorAtField(newSchoolYear, 'immutableDate')"
                                class="errors kgr-color kgr-color--error"
                            >
                                {{ errorMessageForField(newSchoolYear, 'immutableDate') }}
                            </div>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions">
                            <kgr-icon-reset
                                :title="i18n.t('Werte zurücksetzen')"
                                @click="reset(newSchoolYear)"
                            />
                        </td>
                    </tr>
                    <tr
                        v-for="schoolYear in schoolYears"
                        :key="schoolYear.id"
                        :class="{ justSaved: isJustSaved(schoolYear) }"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <template v-if="editing(schoolYear)">
                                <input
                                    v-model="getEdit(schoolYear).name"
                                    type="text"
                                    class="kgr-input"
                                    :maxlength="props.nameMaxLength"
                                    :class="{ 'is-invalid': !isValidName(getEdit(schoolYear)) }"
                                    @blur="save(getEdit(schoolYear))"
                                />
                                <div
                                    v-if="hasErrorAtField(getEdit(schoolYear), 'name')"
                                    class="errors kgr-color kgr-color--error"
                                >
                                    {{ errorMessageForField(getEdit(schoolYear), 'name') }}
                                </div>
                            </template>
                            <template v-else>
                                {{ schoolYear.name }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="editing(schoolYear)">
                                <kgr-date-picker
                                    v-model="getEdit(schoolYear).start"
                                    :invalid="!isValidStart(getEdit(schoolYear))"
                                    @post-input="save(getEdit(schoolYear), 'start')"
                                />
                                <div
                                    v-if="hasErrorAtField(getEdit(schoolYear), 'start')"
                                    class="errors kgr-color kgr-color--error"
                                >
                                    {{ errorMessageForField(getEdit(schoolYear), 'start') }}
                                </div>
                            </template>
                            <template v-else>
                                {{ schoolYear.start }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="editing(schoolYear)">
                                <kgr-date-picker
                                    v-model="getEdit(schoolYear).end"
                                    :invalid="!isValidEnd(getEdit(schoolYear))"
                                    @post-input="save(getEdit(schoolYear), 'end')"
                                />
                                <div
                                    v-if="hasErrorAtField(getEdit(schoolYear), 'end')"
                                    class="errors kgr-color kgr-color--error"
                                >
                                    {{ errorMessageForField(getEdit(schoolYear), 'end') }}
                                </div>
                            </template>
                            <template v-else>
                                {{ schoolYear.end }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="editing(schoolYear)">
                                <kgr-date-picker
                                    v-model="getEdit(schoolYear).immutableDate"
                                    :invalid="!isValidImmutableDate(getEdit(schoolYear))"
                                    @post-input="save(getEdit(schoolYear))"
                                />
                                <div
                                    v-if="hasErrorAtField(getEdit(schoolYear), 'immutableDate')"
                                    class="errors kgr-color kgr-color--error"
                                >
                                    {{ errorMessageForField(getEdit(schoolYear), 'immutableDate') }}
                                </div>
                            </template>
                            <template v-else>
                                {{ schoolYear.immutableDate }}
                            </template>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions">
                            <button
                                type="button"
                                class="kgr-table__action"
                                :disabled="!editing(schoolYear)"
                                @click="editing(schoolYear) && reset(schoolYear)"
                            >
                                <kgr-icon-reset
                                    :disabled="!editing(schoolYear)"
                                    :title="editing(schoolYear) ? i18n.t('Werte zurücksetzen') : '' "
                                />
                            </button>
                            <button
                                type="button"
                                class="kgr-table__action"
                                :disabled="!schoolYear.isEditable"
                                @click="edit(schoolYear)"
                            >
                                <kgr-icon-edit
                                    :disabled="!schoolYear.isEditable"
                                    :title=" schoolYear.isEditable ? i18n.t('edit') : i18n.t('SchoolYear.notEditable', { name: schoolYear.name }) "
                                />
                            </button>
                            <button
                                type="button"
                                class="kgr-table__action"
                                :disabled="!schoolYear.isDeletable"
                                @click="handleRemove(schoolYear)"
                            >
                                <kgr-icon-delete
                                    :disabled="!schoolYear.isDeletable"
                                    :title=" schoolYear.isDeletable ? i18n.t('delete') : i18n.t('SchoolYear.notDeletable', { name: schoolYear.name }) "
                                />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style rel="stylesheet/scss" lang="scss" scoped>
.button_save {
    display: block;
    margin-left: auto;
}
.invalid {
    border-color: #f44336;
}

tr.justSaved td {
    transition: all 0.5s ease-in-out;
    animation: justSaved normal 1s 0s ease-in-out;
}
@keyframes justSaved {
    0% {
        background-color: rgba(255, 255, 0, 0);
    }
    50% {
        background-color: rgba(255, 255, 0, 1);
    }
    100% {
        background-color: rgba(255, 255, 0, 0);
    }
}
</style>
